import { useEffect, useMemo, useState } from 'react';
import { Video, X, Loader2, Play, Link2 } from 'lucide-react';
import { guidesApi, type GuideVideo } from '../lib/api';

interface Props {
  tenantCode: string;
  /** Appends the chosen video's share link to the reply draft. */
  onPaste: (text: string) => void;
  onClose: () => void;
}

/**
 * Pick a video from the guide library and drop its customer-facing link into
 * the reply. This is the "share easily" half of the library — the /guides page
 * is for browsing, this is for the twenty seconds an agent actually has while a
 * customer waits.
 *
 * Only `active` videos are offered: a withdrawn video's link no longer
 * resolves, so putting it in front of an agent mid-conversation would just
 * send a customer to an error page.
 */
export function VideoPickerPanel({ tenantCode, onPaste, onClose }: Props) {
  const [items, setItems] = useState<GuideVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    guidesApi
      .listVideos(tenantCode)
      .then((r) => setItems(r.items.filter((v) => v.active)))
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, [tenantCode]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (v) =>
        v.title.toLowerCase().includes(q) ||
        v.category.toLowerCase().includes(q) ||
        v.description.toLowerCase().includes(q)
    );
  }, [items, filter]);

  return (
    <div className="absolute bottom-full left-0 mb-1 bg-white border border-gray-200 rounded-md shadow-lg w-[420px] max-h-96 overflow-hidden flex flex-col z-20">
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 bg-gray-50">
        <span className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
          <Video className="w-3.5 h-3.5" /> Guide videos
        </span>
        <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="p-2 border-b border-gray-100">
        <input
          type="text"
          autoFocus
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter videos…"
          className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="p-4 text-center text-xs text-gray-500 flex items-center justify-center gap-1.5">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
          </div>
        ) : error ? (
          <div className="p-4 text-center text-xs text-red-600">{error}</div>
        ) : filtered.length === 0 ? (
          <div className="p-4 text-center text-xs text-gray-500">
            {items.length === 0 ? 'No videos in the library yet.' : 'No videos match.'}
          </div>
        ) : (
          filtered.map((v) => (
            <div key={v.id} className="border-b border-gray-100 last:border-0 px-3 py-2 hover:bg-gray-50">
              <div className="flex items-start gap-2">
                <Video className="w-3.5 h-3.5 mt-0.5 text-gray-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-gray-900" dir="auto">
                    {v.title}
                  </div>
                  {v.category && <div className="text-[10px] text-gray-500 mt-0.5">{v.category}</div>}
                </div>
              </div>
              <div className="flex items-center gap-1.5 mt-1.5 ps-5">
                <button
                  type="button"
                  onClick={() => {
                    onPaste(v.shareUrl);
                    onClose();
                  }}
                  className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 bg-primary-600 text-white rounded hover:bg-primary-700"
                >
                  <Link2 className="w-3 h-3" /> Add link to reply
                </button>
                <a
                  href={v.shareUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 text-gray-600 rounded hover:bg-gray-100"
                  title="Watch it first"
                >
                  <Play className="w-3 h-3" /> Preview
                </a>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
